/**
 * Voice session: one ClientSocket (/ws/client) + one VoiceController, alive while the user is
 * linked and the voice page is mounted.
 */
import type { KickReason } from './api/protocol';
import { ClientSocket } from './api/ws';
import { loadPeerPref, loadPrefs } from './prefs';
import { VoiceController } from './voice/VoiceController';

export interface SessionCallbacks {
  onKick(reason: KickReason): void;
  onUnauthorized(): void;
  /** Socket closed before `hello`: the session may have expired. */
  onMaybeUnauthorized(): void;
}

export interface VoiceSession {
  controller: VoiceController;
  socket: ClientSocket;
  dispose(): void;
}

declare global {
  interface Window {
    __VOICE_TEST__?: boolean;
    __voiceTest?: ReturnType<VoiceController['testHook']>;
  }
}

function testHookEnabled(): boolean {
  try {
    return window.__VOICE_TEST__ === true || window.localStorage.getItem('vc:test') === '1';
  } catch {
    return window.__VOICE_TEST__ === true;
  }
}

export function createSession(cb: SessionCallbacks): VoiceSession {
  const prefs = loadPrefs();
  const controller = new VoiceController(
    {},
    {
      micMode: prefs.micMode,
      autoSensitivity: prefs.autoSensitivity,
      vadThreshold: prefs.vadThreshold,
      startMuted: prefs.startMuted,
      inputDeviceId: prefs.inputDeviceId,
      outputDeviceId: prefs.outputDeviceId,
      masterVolume: prefs.masterVolume,
      speakerMode: prefs.speakerMode,
      duckInHeadphones: prefs.duckInHeadphones,
      duckAmount: prefs.duckAmount,
      advancedNoiseSuppression: prefs.advancedNoiseSuppression,
      mono: prefs.audioOutput === 'mono',
      proximity: prefs.proximityEffect,
      peerPref: loadPeerPref,
      // Pause tracks not listed in `peers` (protocol/README.md).
      gateUnassignedTracks: true,
    },
  );
  let beforeHelloCloses = 0;
  const socket = new ClientSocket({
    onMessage: (msg) => {
      if (msg.type === 'hello') beforeHelloCloses = 0;
      controller.handleMessage(msg);
    },
    onStatus: (s) => controller.setSocketStatus(s),
    onKick: (reason) => cb.onKick(reason),
    onUnauthorized: () => cb.onUnauthorized(),
    onClose: (info) => {
      if (info.beforeHello) {
        beforeHelloCloses += 1;
        if (beforeHelloCloses >= 2) cb.onMaybeUnauthorized();
      }
    },
  });
  controller.setSender((m) => socket.send(m));
  if (testHookEnabled()) window.__voiceTest = controller.testHook();
  socket.start();
  return {
    controller,
    socket,
    dispose() {
      socket.stop();
      controller.dispose();
      if (window.__voiceTest) delete window.__voiceTest;
    },
  };
}
